import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { useGetFeaturedPartnersQuery } from '@/api/sanity/generated';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import Loader from '@/components/Loader';
import { type CmsEshopItem, cmsItemsToEshops } from '@/components/boxes/EshopBox/EshopBoxes';
import { type EshopBox } from '@/components/boxes/EshopBox/types';
import { LINKS } from '@/constants/links';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { CategoryDescription } from '@/sections/discounts/CategoryDescription';
import { DiscountsLayout } from '@/sections/discounts/DiscountsLayout';
import { SearchResults } from '@/sections/discounts/SearchResults';
import { texts } from '@/sections/discounts/descriptions/cs';
import { type Category, getCategories } from '@/sections/discounts/functions/getCategories';
import { useDiscountsSearch } from '@/sections/discounts/hooks/useDiscountsSearch';
import { useFormSearch } from '@/sections/discounts/hooks/useFormSearch';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
interface Props {
  category: Category;
  description: string | null;
  categories: Category[];
  emptyCategories: Category[];
  eshops: EshopBox[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(27);
  const t0 = props.category.name;
  let t1;
  if ($[0] !== props.category.slug) {
    t1 = LINKS().discountsCategory(props.category.slug);
    $[0] = props.category.slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.category.name || $[3] !== t1) {
    t2 = {
      title: t0,
      url: t1
    };
    $[2] = props.category.name;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  usePageView("discounts-category", t2);
  let t3;
  if ($[5] !== props.category.id) {
    t3 = {
      category: props.category.id
    };
    $[5] = props.category.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    formValues,
    sync,
    searchForm
  } = useFormSearch(t3);
  let t4;
  if ($[7] !== formValues || $[8] !== props.categories) {
    t4 = {
      formValues,
      categories: props.categories
    };
    $[7] = formValues;
    $[8] = props.categories;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    query,
    breadcrumbs
  } = useDiscountsSearch(t4);
  let t5;
  if ($[10] !== breadcrumbs) {
    t5 = <Breadcrumbs items={breadcrumbs} />;
    $[10] = breadcrumbs;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.description) {
    t6 = props.description && <CategoryDescription text={props.description} />;
    $[12] = props.description;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== props.emptyCategories || $[15] !== query) {
    t7 = query.isLoading ? <Loader /> : <SearchResults emptyCategories={props.emptyCategories} query={query} />;
    $[14] = props.emptyCategories;
    $[15] = query;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== formValues || $[18] !== props.categories || $[19] !== props.eshops || $[20] !== props.menuPromoBanners || $[21] !== searchForm || $[22] !== sync || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <DiscountsLayout menuPromoBanners={props.menuPromoBanners} formValues={formValues} sync={sync} categories={props.categories} eshops={props.eshops} searchForm={searchForm}>{t5}{t6}{t7}</DiscountsLayout>;
    $[17] = formValues;
    $[18] = props.categories;
    $[19] = props.eshops;
    $[20] = props.menuPromoBanners;
    $[21] = searchForm;
    $[22] = sync;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
const emptyCategoriesSlugs = (lang: string) => {
  switch (lang) {
    case 'cs':
      return ['elektronika', 'zdravi-a-krasa', 'deti', 'obuv', 'nabytek'];
    default:
      throw new Error(`Language ${lang} is not supported`);
  }
};
export const getStaticPaths = async () => {
  const categories = await getCategories();
  const paths = categories.map(category => ({
    params: {
      category: category.slug
    }
  }));
  return {
    paths: paths?.filter(Boolean) || [],
    fallback: false
  };
};
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('slevy/kategorie/[:category]');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const categories = await getCategories();
  const emptyCategories = categories.filter(category => emptyCategoriesSlugs(ctx.locale || ctx.defaultLocale || 'cs').includes(category.id));
  const category = categories.find(category => category.slug === ctx.params?.category);
  if (!category) {
    throw new Error('Discount category not found during static generation');
  }
  const description = texts[category.slug];
  const fetchFeaturedPartnersDocument = useGetFeaturedPartnersQuery.fetcher({
    type: 'popular',
    i18nLang: sanityLanguage
  });
  const featuredPartners = await fetchFeaturedPartnersDocument();
  const firstList = featuredPartners.allFeaturedPartnersList[0];
  const popularEshops = cmsItemsToEshops({
    items: firstList.items as CmsEshopItem[],
    imageSize: 'cover',
    size: 'md',
    index: 0,
    section: 'recommended'
  }).splice(0, 4);
  return {
    props: {
      category,
      categories,
      description: description || null,
      emptyCategories,
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      eshops: popularEshops,
      ...(await getIntlProps(ctx, ['discounts']))
    }
  };
}) satisfies GetStaticProps<Props>;